import React, { useMemo } from 'react';
import DictionariesContextProvider from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import { DictionariesProvider } from '@/creditflowresearch/application/Dictionaries/v2/DictionariesContext';
import type { TDictionaryData } from '@/creditflowresearch/application/Dictionaries/v2/useDictionaryAccessors';
import type { TPageDictionaries } from '@/creditflowresearch/application/Dictionaries/DictionaryRegistry';
import { HeaderMenuProvider } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import type { IUseHeaderMenuProps } from '@/creditflowresearch/application/HeaderMenu/useHeaderMenu';
import { IDictionariesState, filterSupportedProducts } from '@/creditflowresearch/application/Dictionaries/helpers';
import UserPlanContextProvider from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { IUserPlanState } from '@/creditflowresearch/application/User/UserPlan/types';
import UserTimeZoneContextProvider from '@/creditflowresearch/application/User/UserTimeZone/UserTimeZoneContext';
import { ITimeZoneInfo } from '@/creditflowresearch/application/User/UserTimeZone/types';
import type { IDomainDictionaries } from '@/creditflowresearch/domain';
import type { IHeaderMenuItems } from '@/creditflowresearch/domain/vo/IHeaderMenuItems';

interface IProps {
  children: React.ReactNode,
  dictionaries?: IDictionariesState,
  userPlanState?: IUserPlanState,
  timeZoneInfo?: ITimeZoneInfo,
  pageDictionaries?: TPageDictionaries,
  headerMenuItems?: IHeaderMenuItems | null,
  domainDictionaries?: IDomainDictionaries,
}

const ApplicationGlobalProps = ({
  children,
  dictionaries,
  userPlanState,
  timeZoneInfo,
  pageDictionaries,
  headerMenuItems,
  domainDictionaries,
}: IProps) => {
  const v2Dictionaries: TDictionaryData = useMemo(() => ({
    currencies: dictionaries?.currencies ?? [],
    countries: dictionaries?.countries ?? [],
    dealStatuses: dictionaries?.dealStatuses ?? [],
    rankings: dictionaries?.rankings ?? [],
    timeZones: dictionaries?.timeZones ?? [],
    ratingAgencies: dictionaries?.ratingAgencies ?? [],
    sizeDescriptions: dictionaries?.sizeDescriptions ?? [],
    ...pageDictionaries,
  }), [dictionaries, pageDictionaries]);

  const headerMenu: IUseHeaderMenuProps = useMemo(() => ({
    products: filterSupportedProducts(domainDictionaries?.products ?? []),
    regions: domainDictionaries?.regions ?? [],
    sectors: pageDictionaries?.sectors ?? [],
    industries: pageDictionaries?.industries ?? [],
    headerMenuItems: headerMenuItems ?? null,
  }), [domainDictionaries, pageDictionaries, headerMenuItems]);

  const content = (
    <UserTimeZoneContextProvider timeZoneInfo={ timeZoneInfo }>
      <UserPlanContextProvider userPlanState={ userPlanState }>
        { children }
      </UserPlanContextProvider>
    </UserTimeZoneContextProvider>
  );

  return (
    <DictionariesProvider dictionaries={ v2Dictionaries }>
      <HeaderMenuProvider headerMenu={ headerMenu }>
        { dictionaries ? (
          <DictionariesContextProvider dictionaries={ dictionaries }>
            { content }
          </DictionariesContextProvider>
        ) : content }
      </HeaderMenuProvider>
    </DictionariesProvider>
  );
};

export default ApplicationGlobalProps;
